import * as React from 'react';
import Card from '../../layout/card/Card';
import { Str } from '../../localization/translationKeys';
import { css } from '@emotion/core';
import LocalDiv from '../../localization/LocalDiv';
import { COLORS } from '../../globalStyle/colors';
import Msg from '../../localization/Msg';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import { ProjStr } from '../messages/ProjStr';
import ProjMsg from '../messages/ProjMsg';
import LocalBtn from '../../localization/LocalBtn';
import { useProjMsgText } from '../messages/hooks';
import { btnStyle } from '../../forms/styles/button';
import { FeedbackSurveyQuestion } from './models';
import { DevPlanItem } from '../devPlan/models';
import CreateSurveyViewAccordian from './CreateSurveyViewAccordian';
import CreateSurveyFootnote from './CreateSurveyFootnote';
import { useWindowSize } from '../../core/hooks/useWindowSize';
import MobileCreateSurveyActionsBar from './MobileCreateSurveyActionsBar';
import { useMemo } from 'react';
import { useAppState } from '../../core/redux/hooks';
import { useProjIds } from '../../navigation/hooks';
import { useMount } from '../../core/hooks/useMount';
import { useDispatch } from 'react-redux';
import { getDevPlanItemsAction } from '../devPlan/api/getDevPlanItemsAction';


const styles = {
  card: css`
    padding: 0;
    border: 3px solid ${COLORS.DARK_GREY};
    height: min-content;
  `,
  halfWidth: css`
    flex-basis: 50%;
  `,
  fullWidth: css`
    flex-basis: 100%;
  `,
  header: css`
    border-radius: 8px 8px 0 0;
    background-color: ${COLORS.DARK_GREY};
    justify-content: space-between;
  `,
  headerText: css`
    padding: 14px;
    font-size: 12px;
    font-weight: bold;
    color: ${COLORS.WHITE};
  `,
  addButton: css`
    font-size: 12px;
    margin: 4px 14px 7px;
    padding: 0 10px;
  `,
  hide: css`
    display: none;
  `,
  noneAdded: css`
    color: ${COLORS.DARK_GREY};
    background-color: ${COLORS.LIGHT_GREEN};
    text-align: center;
    padding: 15px 0;
  `,
  createBtnContainer: css`
    justify-content: flex-end;
  `,
  createButton: css`
    font-size: 12px;
    padding: 0 10px;
    margin: 14px;
  `,
  accordian: css`
    margin: 0;
  `,
};


type Props = {
  addDisabled: boolean;
  count: number;
  createDisabled: boolean;
  createdMode: boolean;
  addedQuestions: FeedbackSurveyQuestion[];
  className?: string;
  deleteQuestion: (questionId: number) => void;
  deleteQuestionsForCompetency: (competencyId: number) => void;
  applyPendingChanges: () => void;
  addByCompetency: boolean;
  toggleAddModal: () => void;
  devItems: DevPlanItem[];
  unsetCreated: () => void;
  fullWidth: boolean;
};


/**
 * CreateSurvey: a component that shows the selected items for a feedback survey
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CreateSurveySelected: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const { isMobile } = useWindowSize();

  const addCaption = useProjMsgText(ProjStr.AddToSurvey);

  const projIds = useProjIds();
  const devItems = useAppState(state => state.devPlan?.items ?? []);

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || projIds.projId);

  useMount(
    () => {
      if (!devPlanProjectId || !projIds.participantId) {
        return;
      }
      !devItems && dispatch(getDevPlanItemsAction(devPlanProjectId, projIds.participantId));
    },
  );  

  const showFootnote = useMemo(
    () => devItems.filter(i => i.devItemCompetencyId > 0).length > 0,
    [devItems],
  )

  return (
    <LocalDiv css={[FLEX_COLUMN, props.fullWidth ? styles.fullWidth : styles.halfWidth]}>
      <Card css={styles.card}>
        <LocalDiv css={[FLEX_ROW, styles.header]}>
          <Msg str={Str.YourSurvey} css={[FLEX_COLUMN, styles.headerText]}/>
          {
            !isMobile && (
              <LocalBtn 
                css={
                  [
                    FLEX_COLUMN,
                    btnStyle.secondary(COLORS.DARK_GREY),
                    styles.addButton,
                    props.createdMode && styles.hide,
                  ]
                }
                str=''
                defaultText={addCaption}
                disabled={props.addDisabled}
                onClick={() => props.toggleAddModal()}
              />
            )
          }
        </LocalDiv>
        {
          props.count === 0
            ? (
                <LocalDiv css={styles.noneAdded}>
                  <ProjMsg str={ProjStr.NoSurveyItemsAdded}/>
                </LocalDiv>
              )
            : (
                <LocalDiv css={styles.accordian}>
                  <CreateSurveyViewAccordian 
                    questions={props.addedQuestions}
                    devItems={props.devItems}
                    addByCompetency={props.addByCompetency}
                    showDelete={!props.createdMode}
                    deleteQuestion={props.deleteQuestion}
                    deleteQuestionsForCompetency={props.deleteQuestionsForCompetency}
                    darkBackground={false}
                    addedStyle={true}
                    createdMode={props.createdMode}
                  />
                </LocalDiv>
              )
        }
        <LocalDiv css={[FLEX_ROW, styles.createBtnContainer]}>
          {
            !isMobile && (
              <LocalBtn 
                css={[btnStyle.primary(props.createdMode ? COLORS.BLUE : COLORS.GREEN), styles.createButton]}
                str={props.createdMode ? Str.EditSurvey : Str.CreateSurvey}
                disabled={props.createDisabled}
                onClick={() => props.createdMode ? props.unsetCreated() : props.applyPendingChanges()}
              />
            )  
          }            
        </LocalDiv>
        {
          isMobile && (
            <MobileCreateSurveyActionsBar
              addDisabled={props.addDisabled}
              addCaption={addCaption}
              addClicked={props.toggleAddModal}
              createDisabled={props.createDisabled}
              createdMode={props.createdMode}
              unsetCreated={props.unsetCreated}
              applyPendingChanges={props.applyPendingChanges}
            />
          )
        }
      </Card>
      {
        showFootnote && <CreateSurveyFootnote />
      }
    </LocalDiv>
  )
};

export default CreateSurveySelected;
